import {
  TourOptionDto,
  TourOptionVariantDto,
  TourOptionVariantPrice,
} from '@/features/dashboard/tourOptions/types/TourOptionDto.ts';
import {
  AgeRestrictionPriceFormData,
  TourOptionFormData,
  TourOptionInputForm,
  VariantFormData,
} from '@/features/dashboard/tourOptions/schemas/tourOptionSchema.ts';
import { ParticipantLimitMode } from '@/features/dashboard/tourOptions/types/ParticipantLimitMode.ts';
import { Currency } from '@/features/dashboard/paymentConfig/types/Currency.ts';

function findNonAgeRestrictedPrice(variant?: TourOptionVariantDto) {
  return variant?.prices?.find((price) => !price.ageRestriction);
}

function extractVariants(tourOptionDto: TourOptionDto): VariantFormData[] {
  return tourOptionDto.variants.map((dto) => {
    const isPriceByAge = dto.prices.some((price) => !!price.ageRestriction);
    const onlyForDefinedAgeRanges = dto.prices.every(
      (price) => !!price.ageRestriction,
    );
    const basePrice = findNonAgeRestrictedPrice(dto);
    return {
      id: dto.id,
      archived: dto.archived,
      basePriceId: basePrice?.id,
      basePriceArchived: basePrice?.archived,
      name: dto.name,
      isPriceByAge: isPriceByAge,
      isVariantOnlyForDefinedAgeRanges: onlyForDefinedAgeRanges,
      participantLimitMode: dto.participantLimit
        ? ParticipantLimitMode.PER_VARIANT
        : ParticipantLimitMode.NONE,
      price: dto.prices[0]?.price?.amount,
      participantLimit: dto.participantLimit,
      ageRestrictionPrices: extractAgeRestricatedPrices(dto),
    };
  });
}

function extractAgeRestricatedPrices(
  firstVariant: TourOptionVariantDto,
): AgeRestrictionPriceFormData[] {
  return (
    firstVariant?.prices
      ?.filter((price) => !!price.ageRestriction)
      .map((price) => ({
        id: price.id,
        archived: price.archived,
        minAge: price.ageRestriction?.minAge,
        maxAge: price.ageRestriction?.maxAge,
        price: price.price?.amount,
        participantLimit: price.participantLimit,
      })) || []
  );
}

export const convertDtoToOptionForm = (
  tourOptionDto: TourOptionDto,
): TourOptionInputForm => {
  const hasVariants =
    tourOptionDto.variants && tourOptionDto.variants.length > 1;
  const firstVariant = tourOptionDto.variants?.[0];
  const hasPriceWithAgeRestriction = firstVariant?.prices?.some(
    (price) => price.ageRestriction,
  );
  //No variant has price without age restriction
  const isVariantOnlyForAges = !tourOptionDto.variants.some((varinat) =>
    varinat.prices.some((price) => !price.ageRestriction),
  );
  const hasMianParticipantLimit = tourOptionDto.participantLimit != null;
  const hasAnyParticipantLimitInVariant = tourOptionDto.variants.some(
    (variant) => variant.participantLimit != null,
  );
  const hasAnyParticpantLimitPerPrice = tourOptionDto.variants.some((variant) =>
    variant.prices.some((price) => price.participantLimit != null),
  );
  const participantLimitMode = hasMianParticipantLimit
    ? ParticipantLimitMode.PER_TOUR_OPTION
    : hasAnyParticipantLimitInVariant
      ? ParticipantLimitMode.PER_VARIANT
      : ParticipantLimitMode.NONE;

  const basePriceIfExists = !hasVariants
    ? firstVariant?.prices?.[0]?.price?.amount
    : undefined;
  const baseCurrencyIfExists =
    firstVariant?.prices?.[0]?.price?.currency || Currency.PLN;
  const baseNonAgePrice = !hasVariants
    ? findNonAgeRestrictedPrice(firstVariant)
    : undefined;

  return {
    archived: tourOptionDto.archived,
    baseVariantId: !hasVariants ? firstVariant?.id : undefined,
    baseVariantArchived: !hasVariants ? firstVariant?.archived : undefined,
    basePriceId: baseNonAgePrice?.id,
    basePriceArchived: baseNonAgePrice?.archived,
    name: tourOptionDto.name,
    description: tourOptionDto.description,
    required: tourOptionDto.required,
    currency: baseCurrencyIfExists,
    hasVariants: hasVariants,
    basePrice: basePriceIfExists,
    isPriceByAge: hasPriceWithAgeRestriction,
    participantLimitMode: participantLimitMode,
    participantLimit: tourOptionDto.participantLimit,
    variants: hasVariants ? extractVariants(tourOptionDto) : [],
    ageRestrictionPrices: hasPriceWithAgeRestriction
      ? extractAgeRestricatedPrices(firstVariant)
      : [],
    allowOptionMultiSelect: tourOptionDto.allowOptionMultiSelect || false,
    isVariantOnlyForDefinedAgeRanges: isVariantOnlyForAges,
  };
};

export const convertOptionFormToDto = (
  tourId: string,
  tourForm: TourOptionFormData,
): Omit<TourOptionDto, 'id' | 'orderIndex'> => {
  const variants: TourOptionVariantDto[] = [];

  if (tourForm.hasVariants) {
    for (const variant of tourForm.variants) {
      const variantDto = createVariantIfMultipleVariants(variant, tourForm);
      variants.push(variantDto);
    }
  } else {
    const variant = createBaseVariant(tourForm);
    variants.push(variant);
  }

  return {
    tourId: tourId,
    name: tourForm.name,
    description: tourForm.description,
    required: tourForm.required,
    participantLimit:
      tourForm.participantLimitMode == ParticipantLimitMode.PER_TOUR_OPTION
        ? tourForm.participantLimit
        : null,
    allowOptionMultiSelect: tourForm.allowOptionMultiSelect,
    archived: tourForm.archived,
    variants: variants,
  };
};

function createVariantIfMultipleVariants(
  variant: VariantFormData,
  tourForm: TourOptionFormData,
): TourOptionVariantDto {
  const prices: TourOptionVariantPrice[] = [];
  if (!variant.isVariantOnlyForDefinedAgeRanges || !variant.isPriceByAge) {
    const basePrice: TourOptionVariantPrice = {
      id: variant.basePriceId,
      archived: variant.basePriceArchived,
      price: {
        amount: variant.price,
        currency: tourForm.currency,
      },
      participantLimit: variant.participantLimit,
    };
    prices.push(basePrice);
  }
  if (variant.isPriceByAge) {
    for (const ageRestriction of variant.ageRestrictionPrices) {
      const agePrice: TourOptionVariantPrice = {
        id: ageRestriction.id,
        archived: ageRestriction.archived,
        price: {
          amount: ageRestriction.price,
          currency: tourForm.currency,
        },
        ageRestriction: {
          minAge: ageRestriction.minAge,
          maxAge: ageRestriction.maxAge,
        },
      };
      prices.push(agePrice);
    }
  }
  return {
    id: variant.id,
    archived: variant.archived,
    name: variant.name,
    participantLimit: variant.participantLimit,
    prices: prices,
  };
}

function createBaseVariant(tourForm: TourOptionFormData): TourOptionVariantDto {
  const prices: TourOptionVariantPrice[] = [];
  //Add non age-restricted price if allowed
  if (!tourForm.isVariantOnlyForDefinedAgeRanges || !tourForm.isPriceByAge) {
    prices.push(getNonAgeRestricatedVarinatPrice(tourForm));
  }
  if (tourForm.isPriceByAge) {
    for (const ageRestriction of tourForm.ageRestrictionPrices) {
      const agePrice: TourOptionVariantPrice = {
        id: ageRestriction.id,
        archived: ageRestriction.archived,
        price: {
          amount: ageRestriction.price,
          currency: tourForm.currency,
        },
        ageRestriction: {
          minAge: ageRestriction.minAge,
          maxAge: ageRestriction.maxAge,
        },
      };
      prices.push(agePrice);
    }
  }
  const baseVarint: TourOptionVariantDto = {
    id: tourForm.baseVariantId,
    archived: tourForm.baseVariantArchived,
    name: tourForm.name,
    prices: prices,
  };

  if (tourForm.participantLimitMode === ParticipantLimitMode.PER_VARIANT) {
    baseVarint.participantLimit = tourForm.participantLimit;
  } else {
    baseVarint.participantLimit = undefined;
  }

  return baseVarint;
}

function getNonAgeRestricatedVarinatPrice(tourForm: TourOptionFormData) {
  const nonAgeRestrictedPric: TourOptionVariantPrice = {
    id: tourForm.basePriceId,
    archived: tourForm.basePriceArchived,
    price: {
      amount: tourForm.basePrice!,
      currency: tourForm.currency,
    },
  };
  nonAgeRestrictedPric.participantLimit = undefined;

  return nonAgeRestrictedPric;
}
